import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Phone, Home, Briefcase, IdCard, FileText, Gauge, Calendar, Globe, MapPin, Receipt, FileSignature, ShieldCheck, Download, Wallet } from "lucide-react";
import api from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const SOLVENCY_STYLE = {
  good: "bg-brand-lime/30 text-brand-greenDeep",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-red-100 text-red-600",
};

function pieceExpiry(dateStr) {
  if (!dateStr) return null;
  const exp = new Date(dateStr);
  if (isNaN(exp)) return null;
  const days = Math.ceil((exp - new Date()) / 86400000);
  if (days < 0) return { label: "Pièce expirée", cls: "bg-red-100 text-red-600" };
  if (days <= 60) return { label: `Expire dans ${days} j`, cls: "bg-amber-100 text-amber-700" };
  return { label: "Valide", cls: "bg-brand-lime/30 text-brand-greenDeep" };
}

export default function LocataireDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/tenants/${id}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="py-20 text-center text-brand-green/50">Locataire introuvable.</div>;
  if (!data) return <div className="py-20 text-center text-brand-green/50">Chargement...</div>;

  const t = data.tenant;
  const s = t.solvency;
  const pe = pieceExpiry(t.piece_expiration);

  return (
    <div className="space-y-6 max-w-5xl">
      <button onClick={() => navigate("/app/locataires")} className="inline-flex items-center gap-2 text-sm text-brand-green/60 hover:text-brand-greenDeep" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Retour aux locataires
      </button>

      {/* Header */}
      <div className="bg-white rounded-2xl p-6 border border-brand-soft flex items-start gap-5" data-testid="tenant-detail-header">
        {t.photo ? (
          <img src={imgSrc(t.photo)} alt={t.name} className="w-20 h-20 rounded-2xl object-cover" />
        ) : (
          <span className="w-20 h-20 rounded-2xl flex items-center justify-center font-semibold text-2xl text-white" style={{ backgroundColor: t.color || "#C9A24B" }}>{t.avatar}</span>
        )}
        <div className="flex-1">
          <h2 className="font-display text-2xl font-semibold text-brand-greenDeep">{t.civilite ? `${t.civilite} ` : ""}{t.name}</h2>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${t.status === "À jour" ? "bg-brand-lime/30 text-brand-greenDeep" : "bg-red-100 text-red-600"}`}>{t.status}</span>
            {t.type_locataire && <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-brand-softer text-brand-green/70">{t.type_locataire}</span>}
            {s && <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold inline-flex items-center gap-1 ${SOLVENCY_STYLE[s.level]}`} data-testid="detail-solvency-badge"><Gauge className="w-3 h-3" /> Solvabilité {s.label}</span>}
          </div>
        </div>
      </div>

      {/* Solvency card */}
      {s && (
        <div className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid="solvency-card">
          <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Gauge className="w-5 h-5" /> Indicateur de solvabilité</h3>
          <div className="flex items-center gap-6 flex-wrap">
            <div>
              <p className="text-xs text-brand-green/50">Revenus mensuels</p>
              <p className="text-xl font-bold text-brand-greenDeep">{s.revenus.toLocaleString("fr-FR")} €</p>
            </div>
            <div>
              <p className="text-xs text-brand-green/50">Loyer CC</p>
              <p className="text-xl font-bold text-brand-greenDeep">{s.rent_cc.toLocaleString("fr-FR")} €</p>
            </div>
            <div>
              <p className="text-xs text-brand-green/50">Taux d'effort</p>
              <p className={`text-xl font-bold ${s.level === "good" ? "text-brand-greenDeep" : s.level === "medium" ? "text-amber-600" : "text-red-600"}`}>{s.ratio}%</p>
            </div>
          </div>
          <div className="mt-4">
            <div className="h-3 rounded-full bg-brand-softer overflow-hidden">
              <div className={`h-full rounded-full ${s.level === "good" ? "bg-brand-limeDark" : s.level === "medium" ? "bg-amber-400" : "bg-red-500"}`} style={{ width: `${Math.min(s.ratio, 100)}%` }} />
            </div>
            <p className="text-xs text-brand-green/50 mt-2">Seuil recommandé : loyer ≤ 33% des revenus (soit {s.effort_max.toLocaleString("fr-FR")} € max). {s.level === "good" ? "Situation confortable." : s.level === "medium" ? "À surveiller." : "Effort locatif élevé."}</p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Personal info */}
        <div className="bg-white rounded-2xl p-6 border border-brand-soft">
          <h3 className="font-semibold text-lg text-brand-greenDeep mb-4">Informations personnelles</h3>
          <div className="space-y-2.5 text-sm text-brand-green/70">
            <Row icon={Mail} value={t.email} />
            <Row icon={Phone} value={t.phone} />
            <Row icon={Home} value={t.property} />
            <Row icon={Calendar} value={t.date_naissance ? `Né(e) le ${new Date(t.date_naissance).toLocaleDateString("fr-FR")}` : null} />
            <Row icon={MapPin} value={t.lieu_naissance} />
            <Row icon={Globe} value={t.nationalite} />
          </div>
        </div>

        {/* Professional + Identity */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-brand-soft">
            <h3 className="font-semibold text-lg text-brand-greenDeep mb-4">Situation professionnelle</h3>
            <div className="space-y-2.5 text-sm text-brand-green/70">
              <Row icon={Briefcase} value={t.profession} />
              <Row icon={Wallet} value={t.revenus_mensuels ? `${Number(t.revenus_mensuels).toLocaleString("fr-FR")} €/mois` : null} />
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-brand-soft">
            <h3 className="font-semibold text-lg text-brand-greenDeep mb-4">Pièce d'identité</h3>
            {t.piece_type ? (
              <div className="space-y-2.5 text-sm text-brand-green/70">
                <p className="flex items-center gap-2.5"><IdCard className="w-4 h-4 text-brand-green/40" /> {t.piece_type}{t.piece_numero ? ` · ${t.piece_numero}` : ""}</p>
                {t.piece_expiration && <p className="flex items-center gap-2.5"><Calendar className="w-4 h-4 text-brand-green/40" /> Expire le {new Date(t.piece_expiration).toLocaleDateString("fr-FR")} {pe && <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${pe.cls}`}>{pe.label}</span>}</p>}
                {t.piece_fichier && <a href={imgSrc(t.piece_fichier)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand-limeDark hover:underline" data-testid="detail-piece-link"><FileText className="w-4 h-4" /> Voir la copie</a>}
              </div>
            ) : <p className="text-sm text-brand-green/50">Aucune pièce renseignée.</p>}
          </div>
        </div>
      </div>

      {/* Signed leases */}
      <div className="bg-white rounded-2xl p-6 border border-brand-soft">
        <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><FileSignature className="w-5 h-5" /> Baux</h3>
        {data.signed_leases.length === 0 && data.leases.length === 0 && <p className="text-sm text-brand-green/50">Aucun bail enregistré.</p>}
        <div className="grid sm:grid-cols-2 gap-3">
          {data.signed_leases.map((l) => (
            <div key={l.token} className="flex items-start gap-3 p-3 rounded-xl border border-brand-soft" data-testid={`detail-lease-${l.token}`}>
              <span className="w-10 h-10 rounded-lg bg-brand-lime/30 flex items-center justify-center text-brand-greenDeep shrink-0"><FileSignature className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-brand-greenDeep truncate">Bail — {l.property_name}</p>
                <p className="text-xs text-brand-green/50">{l.status === "signed" ? `Signé ${l.signed_at || ""}` : "En attente de signature"}</p>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {l.status === "signed" && <a href={imgSrc(l.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-brand-softer text-brand-greenDeep font-semibold hover:bg-brand-soft"><Download className="w-3 h-3" /> Télécharger</a>}
                  {l.sealed && <a href={`/verifier/${l.token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-brand-lime/30 text-brand-greenDeep font-semibold hover:bg-brand-lime/50"><ShieldCheck className="w-3 h-3" /> Horodatage</a>}
                </div>
              </div>
            </div>
          ))}
          {data.leases.map((l) => (
            <div key={l.id} className="flex items-start gap-3 p-3 rounded-xl border border-brand-soft" data-testid={`detail-lease-record-${l.id}`}>
              <span className="w-10 h-10 rounded-lg bg-brand-softer flex items-center justify-center text-brand-green shrink-0"><FileText className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-brand-greenDeep truncate">{l.property} · {l.type}</p>
                <p className="text-xs text-brand-green/50">{l.start} → {l.end} · {(l.rent + (l.charges || 0)).toLocaleString("fr-FR")} € CC</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Receipts */}
      <div className="bg-white rounded-2xl p-6 border border-brand-soft">
        <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Receipt className="w-5 h-5" /> Quittances ({data.receipts.length})</h3>
        <div className="space-y-2">
          {data.receipts.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-2.5 border-b border-brand-soft last:border-0" data-testid={`detail-receipt-${r.id}`}>
              <div>
                <p className="text-sm font-medium text-brand-greenDeep">{r.month}</p>
                <p className="text-xs text-brand-green/50">{r.property}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-brand-greenDeep">{(r.amount || 0).toLocaleString("fr-FR")} €</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${r.status === "Payé" ? "bg-brand-lime/30 text-brand-greenDeep" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
              </div>
            </div>
          ))}
          {data.receipts.length === 0 && <p className="text-sm text-brand-green/50">Aucune quittance.</p>}
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, value }) {
  if (!value) return null;
  return <p className="flex items-center gap-2.5"><Icon className="w-4 h-4 text-brand-green/40" /> {value}</p>;
}
